import React, { useMemo, useState } from "react";
import {
	Alert,
	Button,
	Card,
	Col,
	Modal,
	Row,
	Segmented,
	Space,
	Tag,
	Typography,
} from "antd";
import {
	BulbOutlined,
	CheckOutlined,
	CloseOutlined,
	ExclamationCircleFilled,
	ExperimentOutlined,
	RightOutlined,
	RocketOutlined,
	SearchOutlined,
	ThunderboltOutlined,
} from "@ant-design/icons";
import type { FormInstance } from "antd";
import type { TenantPreferences } from "../../types/tenantPreferences";
import type {
	AssistanceLevel,
	PreferenceDiscoveryResponse,
	SuggestionDiff,
} from "../../types/preferenceDiscovery";
import { useAnalyzePreferences } from "../../hooks/useContentPreferences";

const { Text, Title, Paragraph } = Typography;

// ─── Field label mappings ────────────────────────────────────────────────────

const FIELD_LABELS: Record<string, Record<string, string>> = {
	writing_style: {
		formality: "Formality Level",
		paragraph_length: "Paragraph Length",
		sentence_variety: "Sentence Variety",
		use_questions: "Use Questions",
		use_humor: "Use Humor",
		humanizer_enabled: "Humanizer",
		ai_detection_avoidance: "AI Detection Avoidance",
	},
	tone: {
		primary_tone: "Primary Tone",
		secondary_tone: "Secondary Tone",
	},
	vocabulary: {
		reading_level: "Reading Level",
		industry_jargon_level: "Industry Jargon",
	},
	content_structure: {
		preferred_block_types: "Preferred Block Types",
		avoided_block_types: "Avoided Block Types",
		faq_preference: "FAQ Sections",
		image_preference: "Images",
		table_preference: "Tables",
		min_sections: "Min Sections",
		max_sections: "Max Sections",
		include_key_takeaways: "Key Takeaways",
	},
	links: {
		max_internal_links: "Max Internal Links",
		max_external_links: "Max External Links",
		preferred_anchor_style: "Anchor Style",
		link_density: "Link Density",
	},
	quality_thresholds: {
		min_word_count_override: "Min Word Count",
		max_word_count_override: "Max Word Count",
		target_content_score: "Target Score",
		require_featured_image: "Require Featured Image",
	},
};

const CATEGORY_LABELS: Record<string, string> = {
	writing_style: "Writing Style",
	tone: "Tone",
	vocabulary: "Vocabulary",
	content_structure: "Content Structure",
	links: "Links",
	quality_thresholds: "Quality Thresholds",
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
	writing_style: <BulbOutlined />,
	tone: <ExperimentOutlined />,
	vocabulary: <SearchOutlined />,
	content_structure: <RocketOutlined />,
	links: <ThunderboltOutlined />,
	quality_thresholds: <CheckOutlined />,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatValue(value: unknown): string {
	if (value === null || value === undefined) return "—";
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
	return String(value);
}

function buildDiffs(
	suggestions: Partial<TenantPreferences>,
	current: TenantPreferences | undefined,
	confidence: Record<string, number>,
): SuggestionDiff[] {
	const diffs: SuggestionDiff[] = [];

	for (const [category, fields] of Object.entries(suggestions)) {
		if (!fields || typeof fields !== "object") continue;
		const categoryFields = FIELD_LABELS[category] || {};
		const currentCategory =
			current?.[category as keyof TenantPreferences] ?? {};

		for (const [field, suggestedValue] of Object.entries(
			fields as Record<string, unknown>,
		)) {
			const currentValue =
				typeof currentCategory === "object" && currentCategory !== null
					? (currentCategory as Record<string, unknown>)[field]
					: undefined;

			// Skip fields where the suggestion matches current
			if (JSON.stringify(currentValue) === JSON.stringify(suggestedValue))
				continue;

			diffs.push({
				field: `${category}.${field}`,
				label: categoryFields[field] || field,
				currentValue,
				suggestedValue,
				confidence: confidence[category] ?? 0,
				category,
			});
		}
	}

	return diffs;
}

// ─── Component Props ─────────────────────────────────────────────────────────

interface PreferenceDiscoveryPanelProps {
	form: FormInstance<TenantPreferences>;
	currentPreferences: TenantPreferences | undefined;
	onApplyChanges: () => void;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export const PreferenceDiscoveryPanel: React.FC<
	PreferenceDiscoveryPanelProps
> = ({ form, currentPreferences, onApplyChanges }) => {
	const [level, setLevel] = useState<AssistanceLevel>("medium");
	const [isExpanded, setIsExpanded] = useState(false);
	const [result, setResult] = useState<PreferenceDiscoveryResponse | null>(
		null,
	);
	const [rejectedFields, setRejectedFields] = useState<Set<string>>(
		new Set(),
	);
	const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());

	const analyzeMutation = useAnalyzePreferences();

	const diffs = useMemo(
		() =>
			result
				? buildDiffs(
						result.suggestions,
						currentPreferences,
						result.confidence,
					)
				: [],
		[result, currentPreferences],
	);

	const pendingDiffs = useMemo(
		() =>
			diffs.filter(
				(d) => !rejectedFields.has(d.field) && !appliedFields.has(d.field),
			),
		[diffs, rejectedFields, appliedFields],
	);

	const handleAnalyze = () => {
		setRejectedFields(new Set());
		setAppliedFields(new Set());
		analyzeMutation.mutate(
			{},
			{
				onSuccess: (data) => {
					setResult(data);
					setIsExpanded(true);
				},
			},
		);
	};

	const handleAcceptField = (diff: SuggestionDiff) => {
		const [category, field] = diff.field.split(".");
		form.setFieldValue([category, field], diff.suggestedValue);
		setAppliedFields((prev) => new Set([...prev, diff.field]));
		onApplyChanges();
	};

	const handleRejectField = (field: string) => {
		setRejectedFields((prev) => new Set([...prev, field]));
	};

	const handleApplyAll = () => {
		Modal.confirm({
			title: "Apply AI-Discovered Preferences?",
			icon: <ExclamationCircleFilled />,
			content: `This will update ${pendingDiffs.length} fields across ${
				new Set(pendingDiffs.map((d) => d.category)).size
			} categories. You can review changes in each tab before saving.`,
			okText: "Apply Changes",
			onOk: () => {
				for (const diff of pendingDiffs) {
					const [category, field] = diff.field.split(".");
					form.setFieldValue([category, field], diff.suggestedValue);
				}
				setAppliedFields(
					(prev) =>
						new Set([...prev, ...pendingDiffs.map((d) => d.field)]),
				);
				onApplyChanges();
			},
		});
	};

	const handleAcceptAll = () => {
		for (const diff of pendingDiffs) {
			const [category, field] = diff.field.split(".");
			form.setFieldValue([category, field], diff.suggestedValue);
		}
		setAppliedFields(
			(prev) => new Set([...prev, ...pendingDiffs.map((d) => d.field)]),
		);
		onApplyChanges();
	};

	// ── Render ───────────────────────────────────────────────────────────────

	return (
		<Card
			bordered={false}
			className="bg-warm-bg"
			style={{ marginBottom: 16 }}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Space size={12} align="center">
					<ExperimentOutlined
						style={{
							fontSize: 20,
							color: "var(--color-coral)",
						}}
					/>
					<div>
						<Text strong style={{ fontSize: 15 }}>
							AI Preference Discovery
						</Text>
						<br />
						<Text type="secondary" style={{ fontSize: 12 }}>
							Analyze your published posts to suggest preference
							values
						</Text>
					</div>
				</Space>

				<Space size={12}>
					<Segmented
						options={[
							{ label: "Insights", value: "low" },
							{ label: "Suggestions", value: "medium" },
							{ label: "Auto-fill", value: "high" },
						]}
						value={level}
						onChange={(val) => setLevel(val as AssistanceLevel)}
						size="small"
					/>
					<Button
						type="primary"
						onClick={handleAnalyze}
						loading={analyzeMutation.isPending}
						icon={<SearchOutlined />}
					>
						{result ? "Re-analyze" : "Analyze Posts"}
					</Button>
					{result && (
						<Button
							type="text"
							size="small"
							onClick={() => setIsExpanded(!isExpanded)}
						>
							{isExpanded ? "Collapse" : "Expand"}
						</Button>
					)}
				</Space>
			</div>

			{/* Results */}
			{result && isExpanded && (
				<div style={{ marginTop: 16 }}>
					{/* Summary bar */}
					<div
						style={{
							marginBottom: 16,
							display: "flex",
							gap: 8,
							flexWrap: "wrap",
						}}
					>
						<Tag color="blue">
							{result.posts_analyzed} posts analyzed
						</Tag>
						<Tag color="teal">{diffs.length} suggestions</Tag>
						{appliedFields.size > 0 && (
							<Tag color="green">
								{appliedFields.size} applied
							</Tag>
						)}
						{rejectedFields.size > 0 && (
							<Tag>{rejectedFields.size} rejected</Tag>
						)}
					</div>

					{/* Insights (shown for all levels) */}
					{result.insights.length > 0 && (
						<Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
							{result.insights.map((insight, i) => (
								<Col xs={24} md={12} key={i}>
									<Card
										size="small"
										style={{
											background:
												"var(--color-teal-light)",
											border: "1px solid var(--color-teal)",
										}}
									>
										<Text style={{ fontSize: 13 }}>
											{insight}
										</Text>
									</Card>
								</Col>
							))}
						</Row>
					)}

					{/* Medium: Per-field suggestions */}
					{level === "medium" && diffs.length > 0 && (
						<SuggestionsList
							diffs={diffs}
							appliedFields={appliedFields}
							rejectedFields={rejectedFields}
							onAccept={handleAcceptField}
							onReject={handleRejectField}
							onAcceptAll={handleAcceptAll}
							pendingCount={pendingDiffs.length}
						/>
					)}

					{/* High: Diff summary + apply all */}
					{level === "high" && diffs.length > 0 && (
						<DiffSummary
							diffs={diffs}
							appliedFields={appliedFields}
							pendingCount={pendingDiffs.length}
							onApplyAll={handleApplyAll}
						/>
					)}

					{/* No changes needed */}
					{result.posts_analyzed > 0 && diffs.length === 0 && (
						<Alert
							message="Your current preferences already match your content patterns!"
							type="success"
							showIcon
						/>
					)}

					{/* No posts */}
					{result.posts_analyzed === 0 && (
						<Alert
							message="No published posts found for analysis. Generate and complete some content first."
							type="info"
							showIcon
						/>
					)}
				</div>
			)}
		</Card>
	);
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SuggestionsList: React.FC<{
	diffs: SuggestionDiff[];
	appliedFields: Set<string>;
	rejectedFields: Set<string>;
	onAccept: (diff: SuggestionDiff) => void;
	onReject: (field: string) => void;
	onAcceptAll: () => void;
	pendingCount: number;
}> = ({
	diffs,
	appliedFields,
	rejectedFields,
	onAccept,
	onReject,
	onAcceptAll,
	pendingCount,
}) => {
	// Group by category
	const grouped = useMemo(() => {
		const groups: Record<string, SuggestionDiff[]> = {};
		for (const diff of diffs) {
			if (!groups[diff.category]) groups[diff.category] = [];
			groups[diff.category].push(diff);
		}
		return groups;
	}, [diffs]);

	return (
		<div>
			{Object.entries(grouped).map(([category, categoryDiffs]) => (
				<div key={category} style={{ marginBottom: 16 }}>
					<Space size={8} style={{ marginBottom: 8 }}>
						{CATEGORY_ICONS[category]}
						<Text strong>
							{CATEGORY_LABELS[category] || category}
						</Text>
						<Tag>
							{Math.round(
								(categoryDiffs[0]?.confidence ?? 0) * 100,
							)}
							% confidence
						</Tag>
					</Space>

					{categoryDiffs.map((diff) => {
						const isApplied = appliedFields.has(diff.field);
						const isRejected = rejectedFields.has(diff.field);

						return (
							<div
								key={diff.field}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 12,
									padding: "6px 12px",
									marginBottom: 4,
									borderRadius: 6,
									background: isApplied
										? "var(--color-teal-light)"
										: isRejected
											? "#f5f5f5"
											: "white",
									border: "1px solid #f0f0f0",
									opacity: isRejected ? 0.5 : 1,
								}}
							>
								<Text
									style={{
										width: 160,
										fontSize: 13,
										textDecoration: isRejected
											? "line-through"
											: undefined,
									}}
								>
									{diff.label}
								</Text>
								<Text type="secondary" style={{ fontSize: 13 }}>
									{formatValue(diff.currentValue)}
								</Text>
								<RightOutlined
									style={{
										fontSize: 10,
										color: "#999",
									}}
								/>
								<Text
									strong
									style={{
										fontSize: 13,
										color: "var(--color-coral)",
									}}
								>
									{formatValue(diff.suggestedValue)}
								</Text>
								<div style={{ marginLeft: "auto" }}>
									{isApplied ? (
										<Tag color="green">Applied</Tag>
									) : isRejected ? (
										<Tag>Rejected</Tag>
									) : (
										<Space size={4}>
											<Button
												type="primary"
												size="small"
												icon={<CheckOutlined />}
												onClick={() =>
													onAccept(diff)
												}
											>
												Accept
											</Button>
											<Button
												size="small"
												icon={<CloseOutlined />}
												onClick={() =>
													onReject(diff.field)
												}
											>
												Reject
											</Button>
										</Space>
									)}
								</div>
							</div>
						);
					})}
				</div>
			))}

			{pendingCount > 0 && (
				<div style={{ textAlign: "right", marginTop: 12 }}>
					<Button type="primary" onClick={onAcceptAll}>
						Accept All Remaining ({pendingCount})
					</Button>
				</div>
			)}
		</div>
	);
};

const DiffSummary: React.FC<{
	diffs: SuggestionDiff[];
	appliedFields: Set<string>;
	pendingCount: number;
	onApplyAll: () => void;
}> = ({ diffs, appliedFields, pendingCount, onApplyAll }) => {
	const categories = [...new Set(diffs.map((d) => d.category))];

	return (
		<div>
			<Alert
				message={
					appliedFields.size === diffs.length
						? "All suggestions applied! Review the tabs and save when ready."
						: `Ready to apply ${pendingCount} changes across ${categories.length} categories`
				}
				type={appliedFields.size === diffs.length ? "success" : "info"}
				showIcon
				style={{ marginBottom: 12 }}
			/>

			<div
				style={{
					maxHeight: 300,
					overflow: "auto",
					marginBottom: 12,
				}}
			>
				{diffs.map((diff) => (
					<div
						key={diff.field}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							padding: "4px 8px",
							fontSize: 13,
							background: appliedFields.has(diff.field)
								? "var(--color-teal-light)"
								: undefined,
							borderRadius: 4,
						}}
					>
						<Tag
							color="blue"
							style={{ fontSize: 11, marginRight: 0 }}
						>
							{CATEGORY_LABELS[diff.category] || diff.category}
						</Tag>
						<Text>{diff.label}:</Text>
						<Text type="secondary">
							{formatValue(diff.currentValue)}
						</Text>
						<RightOutlined style={{ fontSize: 10, color: "#999" }} />
						<Text strong style={{ color: "var(--color-coral)" }}>
							{formatValue(diff.suggestedValue)}
						</Text>
						{appliedFields.has(diff.field) && (
							<Tag
								color="green"
								style={{ marginLeft: "auto", fontSize: 11 }}
							>
								Applied
							</Tag>
						)}
					</div>
				))}
			</div>

			{pendingCount > 0 && (
				<Button
					type="primary"
					size="large"
					onClick={onApplyAll}
					block
					icon={<ThunderboltOutlined />}
				>
					Apply All {pendingCount} Suggestions
				</Button>
			)}
		</div>
	);
};
